import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { teamAPI, hubError } from '../lib/api';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ShieldCheck, Loader2, Search, Save, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';

export default function TeamPage() {
  const { canUpdate } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [members, setMembers] = useState([]);
  const [catalog, setCatalog] = useState({ groups: [], permissions: [], presets: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([teamAPI.list(), teamAPI.catalog()])
      .then(([teamRes, catRes]) => {
        setMembers(teamRes.data?.data || []);
        setCatalog(catRes.data?.data || { groups: [], permissions: [], presets: [] });
      })
      .catch(err => {
        if (err?.response?.status === 404) setUnavailable(true);
        else toast.error(hubError(err, 'Error al cargar el equipo'));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openEdit = (member) => {
    setEditing(member);
    setSelected(member.permissions || []);
  };

  const togglePermission = (key) => {
    setSelected(prev => prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]);
  };

  const applyPreset = (preset) => {
    setSelected(preset.permissions || []);
    toast.info(`Preset aplicado: ${preset.label || preset.key}`);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await teamAPI.setPermissions(editing.id, selected);
      toast.success('Permisos actualizados');
      setEditing(null);
      fetchData();
    } catch (err) { toast.error(hubError(err, 'Error al guardar permisos')); } finally { setSaving(false); }
  };

  const term = search.trim().toLowerCase();
  const filtered = term
    ? members.filter(m => `${m.firstname} ${m.lastname} ${m.email}`.toLowerCase().includes(term))
    : members;
  const collaborators = filtered.filter(m => (m.permissions || []).length > 0);
  const pg = usePagination(term);
  const visible = filtered.slice(pg.offset, pg.offset + pg.pageSize);
  const permissionLabel = (key) => catalog.permissions.find(p => p.key === key)?.label || key;

  return (
    <div>
      <TopBar title="Equipo de trabajo" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo} />
      {unavailable ? (
        <div className="px-4 sm:px-6 lg:px-8 py-5">
          <div className="empty-state" data-testid="team-unavailable">
            <ShieldCheck className="w-12 h-12 text-muted-foreground/40" />
            <p className="font-medium">Gestión de equipo aún no disponible</p>
            <p className="text-sm text-muted-foreground max-w-md">
              El servidor de Tu Iglesia App todavía no expone los permisos de equipo en este entorno.
              En cuanto estén publicados, esta sección mostrará a tus colaboradores y sus permisos.
            </p>
          </div>
        </div>
      ) : (
      <div className="px-4 sm:px-6 lg:px-8 py-5">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <Card className="stat-card">
            <CardContent className="p-4">
              <p className="text-2xl font-semibold text-[#056A84]" data-testid="team-total">{members.length}</p>
              <p className="text-xs text-muted-foreground">Miembros aprobados</p>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="p-4">
              <p className="text-2xl font-semibold text-[#1F9D7A]" data-testid="team-collaborators">{collaborators.length}</p>
              <p className="text-xs text-muted-foreground">Con permisos asignados</p>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="p-4">
              <p className="text-2xl font-semibold text-[#2B7DBA]">{catalog.permissions.length}</p>
              <p className="text-xs text-muted-foreground">Permisos disponibles</p>
            </CardContent>
          </Card>
        </div>

        <div className="relative mb-4 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar miembro..." className="pl-9" data-testid="team-search-input" />
        </div>

        <Pagination
          total={filtered.length} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
          label="miembros del equipo" testId="team-pagination"
        />

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 spinner text-[#056A84]" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><ShieldCheck className="w-12 h-12 text-muted-foreground/40" /><p>No hay miembros para mostrar</p></div>
        ) : (
          <div className="space-y-3">
            {visible.map(m => (
              <Card key={m.id} className="stat-card" data-testid={`team-member-${m.id}`}>
                <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    {m.avatar_url ? (
                      <img src={m.avatar_url} alt={m.firstname} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#00CCFF]/10 flex items-center justify-center text-sm font-medium text-[#056A84]">
                        {(m.firstname || 'U')[0]}{(m.lastname || '')[0]}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{m.firstname} {m.lastname}</p>
                      <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(m.permissions || []).length === 0 ? (
                          <span className="text-[10px] text-muted-foreground">Sin permisos asignados</span>
                        ) : m.permissions.map(p => (
                          <Badge key={p} variant="secondary" className="text-[10px]">{permissionLabel(p)}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  {canUpdate('team') && (
                    <Button variant="outline" size="sm" onClick={() => openEdit(m)} data-testid={`team-edit-${m.id}`}>
                      <UserCog className="w-4 h-4 mr-1" /> Permisos
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      )}

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Permisos · {editing?.firstname} {editing?.lastname}</DialogTitle>
            <DialogDescription>Marca lo que este colaborador podrá gestionar en la app y en la Suite</DialogDescription>
          </DialogHeader>

          {catalog.presets?.length > 0 && (
            <div className="flex flex-wrap gap-2 pb-2">
              {catalog.presets.map(preset => (
                <Button key={preset.key} variant="outline" size="sm" className="text-xs" onClick={() => applyPreset(preset)} data-testid={`team-preset-${preset.key}`}>
                  {preset.label || preset.key}
                </Button>
              ))}
            </div>
          )}

          <div className="space-y-4">
            {catalog.groups.map(group => {
              const perms = catalog.permissions.filter(p => p.group === group.key);
              if (perms.length === 0) return null;
              return (
                <div key={group.key}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{group.label}</p>
                  <div className="space-y-2">
                    {perms.map(p => (
                      <label key={p.key} className="flex items-start gap-3 p-2 rounded-lg border cursor-pointer hover:bg-accent/40">
                        <Checkbox
                          checked={selected.includes(p.key)}
                          onCheckedChange={() => togglePermission(p.key)}
                          data-testid={`team-permission-${p.key}`}
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium">{p.label}</span>
                          {p.description && <span className="block text-xs text-muted-foreground">{p.description}</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected([])} data-testid="team-clear-button">Quitar todos</Button>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white" onClick={handleSave} disabled={saving} data-testid="team-save-button">
              {saving ? <Loader2 className="w-4 h-4 spinner mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Guardar permisos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
